import api from '../api';

export type AnalyticsV2EventName =
  | 'page_view'
  | 'heartbeat'
  | 'search_performed'
  | 'ticker_viewed'
  | 'tab_changed'
  | 'screener_filtered'
  | 'watchlist_updated'
  | 'login_succeeded'
  | 'dcf_started'
  | 'stock_comparison_started'
  | 'dcf_analysis_saved'
  | 'dcf_comment_added'
  | 'dcf_exported';

type Properties = Record<string, unknown>;

type AttributionTouch = {
  source: string;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  clickId: string | null;
  referrer: string | null;
  landingPage: string;
  capturedAt: string;
};

type StoredAttribution = {
  firstTouch: AttributionTouch;
  lastTouch: AttributionTouch;
};

type StoredSession = {
  id: string;
  startedAt: number;
  lastActivityAt: number;
};

type QueuedEvent = {
  eventId: string;
  name: AnalyticsV2EventName;
  occurredAt: string;
  anonymousId: string;
  sessionId: string;
  viewId: string;
  page: {
    path: string;
    search: string;
    title: string;
  };
  attribution: StoredAttribution;
  client: Record<string, unknown>;
  properties: Properties;
  activeMs: number;
  requiresAuth?: boolean;
};

const ANONYMOUS_KEY = 'insicard_analytics_v2_anonymous_id';
const SESSION_KEY = 'insicard_analytics_v2_session';
const ATTRIBUTION_KEY = 'insicard_analytics_v2_attribution';
const QUEUE_KEY = 'insicard_analytics_v2_queue';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const USER_ACTIVE_WINDOW_MS = 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 15 * 1000;
const FLUSH_INTERVAL_MS = 10 * 1000;
const MAX_QUEUE_SIZE = 250;
const MAX_BATCH_SIZE = 40;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);
const ANALYTICS_V2_DISABLED =
  process.env.NODE_ENV !== 'production' ||
  LOCAL_HOSTNAMES.has(window.location.hostname.toLowerCase());
export const ANALYTICS_V2_AUTH_REJECTED_EVENT =
  'analytics-v2-auth-rejected';

const SENSITIVE_QUERY_KEY =
  /(token|secret|password|credential|authorization|^code$|^state$)/i;

export const sanitizeAnalyticsSearch = (value: string): string => {
  if (!value) return '';
  try {
    const params = new URLSearchParams(value);
    params.forEach((_parameterValue, key) => {
      if (SENSITIVE_QUERY_KEY.test(key)) params.set(key, '[redacted]');
    });
    const sanitized = params.toString();
    return sanitized ? `?${sanitized}`.slice(0, 500) : '';
  } catch {
    return '';
  }
};

const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Analytics must never affect the product experience.
    }
  },
};

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const cleanReferrer = (value: string): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`.slice(0, 500);
  } catch {
    return value.slice(0, 500);
  }
};

const currentTouch = (): AttributionTouch => {
  const params = new URLSearchParams(window.location.search);
  const referrer = cleanReferrer(document.referrer);
  let referrerHost = '';
  if (referrer) {
    try {
      referrerHost = new URL(referrer).hostname.replace(/^www\./, '');
    } catch {
      referrerHost = '';
    }
  }
  const source = params.get('utm_source') || referrerHost || 'direct';
  const medium =
    params.get('utm_medium') || (referrerHost ? 'referral' : null);

  return {
    source: source.slice(0, 120),
    medium: medium?.slice(0, 120) || null,
    campaign: params.get('utm_campaign')?.slice(0, 160) || null,
    term: params.get('utm_term')?.slice(0, 160) || null,
    content: params.get('utm_content')?.slice(0, 160) || null,
    clickId:
      params.get('gclid')?.slice(0, 200) ||
      params.get('fbclid')?.slice(0, 200) ||
      null,
    referrer,
    landingPage: `${window.location.pathname}${sanitizeAnalyticsSearch(
      window.location.search
    )}`.slice(0, 500),
    capturedAt: new Date().toISOString(),
  };
};

const hasCampaignParameters = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
  ].some(key => params.has(key));
};

const getClientContext = (): Record<string, unknown> => {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { effectiveType?: string };
    userAgentData?: { mobile?: boolean; platform?: string };
  };
  return {
    language: nav.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    pixelRatio: window.devicePixelRatio,
    touchPoints: nav.maxTouchPoints || 0,
    platform: nav.userAgentData?.platform || nav.platform || null,
    mobileHint: nav.userAgentData?.mobile ?? null,
    deviceMemory: nav.deviceMemory ?? null,
    connection: nav.connection?.effectiveType || null,
  };
};

class AnalyticsV2Tracker {
  private initialized = false;
  private anonymousId = '';
  private queue: QueuedEvent[] = [];
  private currentViewId = createId();
  private currentPath = window.location.pathname;
  private currentSearch = sanitizeAnalyticsSearch(window.location.search);
  private currentViewStartedAt = 0;
  private lastUserActivityAt = Date.now();
  private lastHeartbeatAt = Date.now();
  private flushing = false;
  private attribution: StoredAttribution | null = null;
  private debounceTimers = new Map<string, number>();

  initialize() {
    if (ANALYTICS_V2_DISABLED) return;
    if (this.initialized) return;
    this.initialized = true;
    this.anonymousId = this.getAnonymousId();
    this.queue = parseJson<QueuedEvent[]>(safeStorage.get(QUEUE_KEY)) || [];
    this.queue = this.queue.slice(-MAX_QUEUE_SIZE);
    this.attribution = this.getAttribution();

    const noteActivity = () => {
      this.lastUserActivityAt = Date.now();
    };
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(eventName => {
      window.addEventListener(eventName, noteActivity, { passive: true });
    });

    document.addEventListener(
      'click',
      event => {
        const target = event.target as Element | null;
        const tab = target?.closest?.('[role="tab"]');
        if (!tab) return;
        this.track('tab_changed', {
          component: 'tab',
          tab:
            tab.getAttribute('aria-label') ||
            tab.textContent?.trim().slice(0, 120) ||
            tab.getAttribute('data-value') ||
            'unknown',
        });
      },
      true
    );

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.heartbeat(true);
        void this.flush(true);
      } else {
        this.lastHeartbeatAt = Date.now();
      }
    });
    window.addEventListener('pagehide', () => {
      this.heartbeat(true);
      void this.flush(true);
    });

    window.setInterval(() => this.heartbeat(), HEARTBEAT_INTERVAL_MS);
    window.setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    if (this.queue.length) void this.flush();
  }

  startPageView(path: string, search = '', title = document.title) {
    if (ANALYTICS_V2_DISABLED) return this.currentViewId;
    this.initialize();
    const sanitizedSearch = sanitizeAnalyticsSearch(search);
    const now = Date.now();
    const isStrictModeReplay =
      path === this.currentPath &&
      sanitizedSearch === this.currentSearch &&
      now - this.currentViewStartedAt < 1000;
    if (isStrictModeReplay) return this.currentViewId;

    this.currentPath = path;
    this.currentSearch = sanitizedSearch;
    this.currentViewId = createId();
    this.currentViewStartedAt = now;
    this.lastHeartbeatAt = now;
    this.track('page_view', {}, 0, title);
    return this.currentViewId;
  }

  track(
    name: AnalyticsV2EventName,
    properties: Properties = {},
    activeMs = 0,
    title = document.title
  ) {
    if (ANALYTICS_V2_DISABLED) return;
    this.initialize();
    const session = this.getSession();
    if (session.isNew && this.currentViewStartedAt && name !== 'page_view') {
      this.currentViewId = createId();
      this.currentViewStartedAt = Date.now();
      this.lastHeartbeatAt = Date.now();
      this.track('page_view', { reason: 'session_timeout' }, 0, title);
    }
    const event: QueuedEvent = {
      eventId: createId(),
      name,
      occurredAt: new Date().toISOString(),
      anonymousId: this.anonymousId,
      sessionId: session.id,
      viewId: this.currentViewId,
      page: {
        path: this.currentPath.slice(0, 500),
        search: this.currentSearch.slice(0, 500),
        title: title.slice(0, 300),
      },
      attribution: this.attribution || this.getAttribution(),
      client: getClientContext(),
      properties,
      activeMs: Math.max(0, Math.min(HEARTBEAT_INTERVAL_MS * 2, activeMs)),
      requiresAuth: Boolean(safeStorage.get('token')),
    };

    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE_SIZE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE);
    }
    this.persistQueue();
    if (this.queue.length >= 10) void this.flush();
  }

  trackDebounced(
    name: AnalyticsV2EventName,
    properties: Properties,
    delayMs = 700,
    key: string = name
  ) {
    if (ANALYTICS_V2_DISABLED) return;
    const previous = this.debounceTimers.get(key);
    if (previous) window.clearTimeout(previous);
    const timer = window.setTimeout(() => {
      this.debounceTimers.delete(key);
      this.track(name, properties);
    }, delayMs);
    this.debounceTimers.set(key, timer);
  }

  getViewId() {
    return this.currentViewId;
  }

  authenticationAvailable() {
    if (ANALYTICS_V2_DISABLED) return;
    this.initialize();
    window.setTimeout(() => void this.flush(), 0);
  }

  private getAnonymousId(): string {
    const stored = safeStorage.get(ANONYMOUS_KEY);
    if (stored && stored.length >= 16 && stored.length <= 80) return stored;
    const generated = createId();
    safeStorage.set(ANONYMOUS_KEY, generated);
    return generated;
  }

  private getSession(): StoredSession & { isNew: boolean } {
    const now = Date.now();
    const stored = parseJson<StoredSession>(safeStorage.get(SESSION_KEY));
    const isValid =
      stored &&
      typeof stored.id === 'string' &&
      now - Number(stored.lastActivityAt) <= SESSION_TIMEOUT_MS;
    const session: StoredSession = isValid
      ? { ...stored, lastActivityAt: now }
      : { id: createId(), startedAt: now, lastActivityAt: now };
    safeStorage.set(SESSION_KEY, JSON.stringify(session));
    return { ...session, isNew: !isValid };
  }

  private getAttribution(): StoredAttribution {
    const stored = parseJson<StoredAttribution>(safeStorage.get(ATTRIBUTION_KEY));
    const touch = currentTouch();
    const shouldRefreshLastTouch = hasCampaignParameters() || !stored;
    const next: StoredAttribution = {
      firstTouch: stored?.firstTouch || touch,
      lastTouch: shouldRefreshLastTouch ? touch : stored.lastTouch,
    };
    safeStorage.set(ATTRIBUTION_KEY, JSON.stringify(next));
    return next;
  }

  private heartbeat(force = false) {
    const now = Date.now();
    const elapsed = Math.max(
      0,
      Math.min(HEARTBEAT_INTERVAL_MS * 2, now - this.lastHeartbeatAt)
    );
    this.lastHeartbeatAt = now;
    if (elapsed < 250) return;
    if (
      (!force && document.visibilityState !== 'visible') ||
      now - this.lastUserActivityAt > USER_ACTIVE_WINDOW_MS ||
      !this.currentViewStartedAt
    ) {
      return;
    }
    this.track('heartbeat', {}, elapsed);
  }

  private persistQueue() {
    safeStorage.set(QUEUE_KEY, JSON.stringify(this.queue));
  }

  private endpoint(): string {
    const base = String(api.defaults.baseURL || '/api/');
    return `${base.endsWith('/') ? base : `${base}/`}analytics/v2/batch`;
  }

  private async postBatch(
    batch: QueuedEvent[],
    keepalive: boolean,
    token: string | null
  ) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(this.endpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: batch }),
      keepalive,
      credentials: 'omit',
    });
  }

  async flush(keepalive = false) {
    if (ANALYTICS_V2_DISABLED) return;
    if (this.flushing || this.queue.length === 0) return;
    this.flushing = true;
    const token = safeStorage.get('token');
    const batch = this.queue
      .filter(event => Boolean(token) || !event.requiresAuth)
      .slice(0, MAX_BATCH_SIZE);
    try {
      if (!batch.length) return;
      const response = await this.postBatch(batch, keepalive, token);
      if (response.status === 401 && token) {
        window.dispatchEvent(
          new CustomEvent(ANALYTICS_V2_AUTH_REJECTED_EVENT)
        );
        return;
      }
      if (response.ok || response.status === 400 || response.status === 413) {
        const completedIds = new Set(batch.map(event => event.eventId));
        this.queue = this.queue.filter(
          event => !completedIds.has(event.eventId)
        );
        this.persistQueue();
      }
    } catch {
      // The persisted queue is retried on the next interval or app launch.
    } finally {
      this.flushing = false;
    }
  }
}

export const analyticsV2 = new AnalyticsV2Tracker();
